import { Form, Select, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { useDistrictOptions, useRegionOptions } from './useGeoOptions'

export interface GeoFallback {
  id: string
  name: string
}

interface RegionDistrictIdFieldsProps {
  regionPath: (string | number)[]
  districtPath: (string | number)[]
  disabled?: boolean
  /** Reyestr sahifasidan tashqarida qolgan joriy qiymat (server bergan nom) */
  fallbackRegion?: GeoFallback | null
  fallbackDistrict?: GeoFallback | null
}

/**
 * Viloyat/tuman tanlagichlari, qiymat sifatida **ID** beradi.
 *
 * ⚠️ {@link RegionDistrictFields} dan farqi faqat shunda: u `/api/sites`
 * shartnomasi tufayli NOM saqlaydi, kontakt esa `regionId`/`districtId`
 * ustunlariga yozadi (GEO §3). Ikkalasi bitta `useGeoOptions` ni
 * ishlatadi — ro'yxat va kesh bitta joydan keladi.
 *
 * ⚠️ QO'SHISH YO'Q — faqat tanlash (GEO §6).
 */
export function RegionDistrictIdFields({
  regionPath,
  districtPath,
  disabled = false,
  fallbackRegion = null,
  fallbackDistrict = null,
}: RegionDistrictIdFieldsProps) {
  const { t } = useTranslation()
  const form = Form.useFormInstance()
  const regionId = Form.useWatch<string | undefined>(regionPath, form) ?? null
  const districtId = Form.useWatch<string | undefined>(districtPath, form) ?? null

  // ⚠️ `activeOnly=false` — «yopilganlar» qoidasi (ADR-033) bu tanlagichga
  // QO'LLANMAYDI. Nofaol viloyat ro'yxatdan tushib qolsa, unga biriktirilgan
  // mavjud kontakt ekranda BO'SH ko'rinardi va birinchi saqlashda bog'lanish
  // jimgina yo'qolardi.
  const regions = useRegionOptions(false)
  const districts = useDistrictOptions(regionId, false)

  const regionOptions = withCurrent(regions.options, regionId, fallbackRegion, t('geo.inactive'))
  const districtOptions = withCurrent(
    districts.options,
    districtId,
    fallbackDistrict,
    t('geo.inactive'),
  )

  const registryEmpty = !regions.isPending && !regions.isError && regions.options.length === 0

  return (
    <>
      <Form.Item
        name={regionPath}
        label={t('geo.region')}
        extra={
          registryEmpty ? (
            <Typography.Text type="warning">{t('geo.registryEmpty')}</Typography.Text>
          ) : regions.truncated ? (
            <Typography.Text type="warning">{t('geo.optionsTruncated')}</Typography.Text>
          ) : (
            t('geo.selectOnlyHint')
          )
        }
      >
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          disabled={disabled}
          loading={regions.isPending}
          placeholder={t('geo.regionPlaceholder')}
          options={regionOptions}
          // ⚠️ Viloyat o'zgarsa tuman TOZALANADI. Usiz «Kobul viloyati +
          // Toshkent tumani» juftligi saqlanardi: bazada kompozit tekshiruv
          // uni rad etadi va operator sababsiz 400 ko'rardi.
          onChange={() => form.setFieldValue(districtPath, undefined)}
        />
      </Form.Item>

      <Form.Item name={districtPath} label={t('geo.district')}>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          // Viloyat tanlanmaguncha o'chiq: tuman ro'yxati viloyatsiz ma'nosiz
          disabled={disabled || regionId === null}
          loading={districts.isPending}
          placeholder={t(regionId === null ? 'geo.districtDisabled' : 'geo.districtPlaceholder')}
          options={districtOptions}
        />
      </Form.Item>
    </>
  )
}

/**
 * Joriy qiymatni ro'yxatda BOR qiladi.
 *
 * ⚠️ Ikki holatda kerak: ro'yxat chegaradan oshgan (`OPTIONS_SIZE`) va
 * yozuv nofaol qilingan. Ikkalasida ham tanlagich bo'sh ko'rinardi, saqlash
 * esa bog'lanishni o'chirardi — hech qanday xatosiz.
 */
function withCurrent(
  options: readonly { id: string; name: string; active: boolean }[],
  current: string | null,
  fallback: GeoFallback | null,
  inactiveNote: string,
) {
  const mapped = options.map((option) => ({
    value: option.id,
    label: option.active ? option.name : `${option.name} — ${inactiveNote}`,
  }))
  if (current !== null && !options.some((option) => option.id === current)) {
    const name = fallback?.id === current ? fallback.name : current
    mapped.unshift({ value: current, label: name })
  }
  return mapped
}
